import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ClientFormDialog from "@/components/ClientFormDialog";
import BirthdaysTab from "@/components/BirthdaysTab";
import { toast } from "sonner";
import { buildWaUrl, genericMessage } from "@/lib/whatsapp";
import { DisplayName } from "@/lib/name";
import { Users, Cake } from "lucide-react";
import {
 AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
 AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function formatBR(date) {
 if (!date) return "";
 const [y, m, d] = date.split("-");
 return `${d}/${m}/${y}`;
}

function maskCpf(cpf) {
 const v = (cpf || "").replace(/\D/g, "");
 if (v.length !== 11) return cpf;
 return `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6,9)}-${v.slice(9,11)}`;
}

function fmtDT(iso) {
 if (!iso) return "";
 return new Date(iso).toLocaleString("pt-BR", {
 day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
 });
}

function ClientRow({ c, expanded, onToggle, onEdit, onDelete, selected, onSelectToggle }) {
 const [emissions, setEmissions] = useState(null);
 const [loadingE, setLoadingE] = useState(false);
 const waUrl = c.phone ? buildWaUrl(c.phone, genericMessage(c)) : "";

 useEffect(() =>{
 if (!expanded || emissions !== null) return;
 setLoadingE(true);
 api.get(`/clients/${c.client_id}/emissions`)
 .then(({ data }) =>setEmissions(data))
 .catch(() =>setEmissions([]))
 .finally(() =>setLoadingE(false));
 }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

 const handleCopy = async () =>{
 const text = `${c.full_name}\n${formatBR(c.birth_date)}\n${maskCpf(c.cpf)}`;
 try {
 await navigator.clipboard.writeText(text);
 toast.success("Dados copiados ");
 } catch {
 toast.error("Não foi possível copiar");
 }
 };

 return (
  <div className={`border-b border-slate-100 last:border-b-0 flex items-stretch ${selected ? "bg-orange-50/60" : ""}`} data-testid={`client-row-${c.client_id}`}>
    <label
      className="flex items-center px-3 hover:bg-orange-50 cursor-pointer shrink-0"
      onClick={(e) => e.stopPropagation()}
      data-testid={`client-select-label-${c.client_id}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onSelectToggle(c.client_id)}
        className="w-4 h-4 accent-orange-500 cursor-pointer"
        data-testid={`client-select-${c.client_id}`}
      />
    </label>
    <div className="flex-1 min-w-0">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full grid grid-cols-12 items-center gap-2 pr-4 py-3 text-left hover:bg-blue-50/40 transition ${expanded ? "bg-blue-50/40" : ""}`}
        data-testid={`client-toggle-${c.client_id}`}
      ><div className="col-span-12 sm:col-span-5 flex items-center gap-2 min-w-0"><span className={`text-slate-400 text-xs transition-transform ${expanded ? "rotate-90" : ""}`} aria-hidden>▶</span><span className="w-8 h-8 rounded-full bg-blue-100 text-[#0b3a82] grid place-items-center text-sm font-bold shrink-0">{(c.full_name || "?")[0].toUpperCase()}</span><div className="min-w-0"><div className="font-semibold text-slate-900 truncate"><DisplayName name={c.full_name} /></div><div className="text-xs text-slate-500 truncate">{maskCpf(c.cpf)}</div></div></div><div className="hidden sm:block sm:col-span-3 text-sm text-slate-600 truncate">{c.phone}</div><div className="hidden sm:block sm:col-span-2 text-xs text-slate-500 truncate">{c.email || "—"}</div><div className="hidden sm:block sm:col-span-2 text-xs text-slate-500 truncate">{formatBR(c.birth_date)}</div></button>{expanded && (<div className="px-4 pb-4 pt-2 bg-white border-t border-slate-100" data-testid={`client-detail-${c.client_id}`}><div className="flex flex-wrap gap-2 mb-4"><Button size="sm" variant="outline"
 onClick={handleCopy}
 className="border-blue-200 text-[#0b3a82] hover:bg-blue-50"
 data-testid={`copy-client-${c.client_id}`}>Copiar dados</Button>{waUrl && (<a href={waUrl} target="_blank" rel="noreferrer"
 data-testid={`whatsapp-client-${c.client_id}`}><Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">WhatsApp</Button></a>)}<Button size="sm" variant="ghost" onClick={() =>onEdit(c)}
 data-testid={`edit-client-${c.client_id}`}>Editar</Button><Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
 onClick={() =>onDelete(c)}
 data-testid={`delete-client-${c.client_id}`}>Excluir</Button></div>{c.notes && (<div className="text-sm text-slate-600 italic mb-3">&ldquo;{c.notes}&rdquo;</div>)}<div><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Voos próximos</div>{loadingE ? (<div className="text-slate-400 text-sm">Carregando...</div>) : (emissions?.length ?? 0) === 0 ? (<div className="text-slate-400 text-sm" data-testid={`client-no-flights-${c.client_id}`}>Sem voos próximos.</div>) : (<div className="space-y-2" data-testid={`client-flights-${c.client_id}`}>{emissions.map((e) =>(<div key={e.emission_id}
 className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 text-sm"><div className="flex items-start justify-between gap-2"><div className="font-semibold text-slate-900">{e.origin}<span className="text-blue-500">→</span>{e.destination}
 {e.return_at &&<span className="text-slate-400 text-xs ml-2">ida e volta</span>}</div><span className="pill pill-blue text-xs">Loc. {e.pnr}</span></div><div className="text-xs text-slate-500 mt-1">{fmtDT(e.departure_at)}
 {e.return_at &&<>·  {fmtDT(e.return_at)}</>}
 {e.airline &&<>· {e.airline}</>}</div></div>))}</div>)}</div></div>)}
    </div>
  </div>);
}

export default function ClientsTab({ onChange, autoOpenSignal = 0, initialSearch = "" }) {
 const [clients, setClients] = useState([]);
 const [loading, setLoading] = useState(true);
 const [search, setSearch] = useState(initialSearch);
 const [openForm, setOpenForm] = useState(false);
 const [editing, setEditing] = useState(null);
 const [deleting, setDeleting] = useState(null);
 const [expandedId, setExpandedId] = useState(null);
 const [selectedIds, setSelectedIds] = useState([]);

 const toggleSelect = (id) => {
   setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
 };
 const clearSelection = () => setSelectedIds([]);

 const copyAllSelected = async () => {
   const selected = selectedIds
     .map((id) => clients.find((c) => c.client_id === id))
     .filter(Boolean);
   if (selected.length === 0) return;
   const text = selected
     .map((c, i) => `Pax ${i + 1}:\n${c.full_name}\n${formatBR(c.birth_date)}\n${maskCpf(c.cpf)}`)
     .join("\n\n");
   try {
     await navigator.clipboard.writeText(text);
     toast.success(`${selected.length} passageiro(s) copiado(s)`);
   } catch {
     toast.error("Não foi possível copiar");
   }
 };

 useEffect(() =>{
   if (autoOpenSignal > 0) {
     setEditing(null);
     setOpenForm(true);
   }
 }, [autoOpenSignal]);

 useEffect(() =>{
   if (initialSearch) setSearch(initialSearch);
 }, [initialSearch]);

 const load = async () =>{
 setLoading(true);
 try {
 const { data } = await api.get("/clients");
 setClients(data);
 } catch (e) {
 toast.error(formatApiError(e.response?.data?.detail));
 } finally {
 setLoading(false);
 }
 };

 useEffect(() =>{ load(); }, []);

 const handleDelete = async () =>{
 if (!deleting) return;
 try {
 await api.delete(`/clients/${deleting.client_id}`);
 toast.success("Cliente excluído");
 setDeleting(null);
 load();
 onChange?.();
 } catch (e) {
 toast.error(formatApiError(e.response?.data?.detail));
 }
 };

 const q = search.toLowerCase().trim();
 const filtered = clients.filter((c) =>{
 if (!q) return true;
 return (
 c.full_name.toLowerCase().includes(q) ||
 (c.cpf || "").includes(q) ||
 (c.email || "").toLowerCase().includes(q) ||
 (c.family || "").toLowerCase().includes(q)
 );
 });

 // Group by family
 const groups = new Map();
 for (const c of filtered) {
 const fam = (c.family || "").trim();
 const key = fam || "__none__";
 if (!groups.has(key)) groups.set(key, []);
 groups.get(key).push(c);
 }
 // Sort: named families A→Z first, then "Sem família" last
 const groupKeys = Array.from(groups.keys()).sort((a, b) =>{
 if (a === "__none__") return 1;
 if (b === "__none__") return -1;
 return a.localeCompare(b, "pt-BR");
 });

 return (<div data-testid="clients-tab"><Tabs defaultValue="list"><div className="flex items-center justify-between gap-3 mb-4 flex-wrap"><div><h2 className="text-xl font-bold display text-slate-900">Clientes</h2><p className="text-slate-500 text-sm">Cadastros, famílias e dados para emissão.</p></div><div className="flex items-center gap-3"><TabsList className="bg-white border border-slate-200"><TabsTrigger value="list" data-testid="clients-subtab-list"><Users className="w-4 h-4 mr-1.5" />Lista</TabsTrigger><TabsTrigger value="birthdays" data-testid="clients-subtab-birthdays"><Cake className="w-4 h-4 mr-1.5" />Aniversariantes</TabsTrigger></TabsList><Button onClick={() =>{ setEditing(null); setOpenForm(true); }}
 className="h-11 brand-grad text-white hover:opacity-95"
 data-testid="add-client-button">Novo cliente</Button></div></div><TabsContent value="list" className="mt-0"><Input placeholder="Buscar por nome, CPF, email ou família"
 value={search} onChange={(e) =>setSearch(e.target.value)}
 className="h-11 bg-white mb-4"
 data-testid="clients-search-input" />
 {selectedIds.length > 0 && (
   <div className="mb-4 rounded-xl border border-orange-200 bg-orange-50 p-3 flex flex-wrap items-center gap-3 shadow-sm"
        data-testid="clients-selection-bar">
     <div className="text-sm font-semibold text-orange-900">
       {selectedIds.length} passageiro(s) selecionado(s)
     </div>
     <div className="flex items-center gap-2 ml-auto">
       <Button size="sm" onClick={copyAllSelected}
         className="orange-grad text-white hover:opacity-95"
         data-testid="copy-selected-clients">
         Copiar todos
       </Button>
       <Button size="sm" variant="ghost" onClick={clearSelection}
         data-testid="clear-selection">
         Limpar
       </Button>
     </div>
   </div>
 )}
 {loading ? (<div className="text-slate-500 text-center py-12" data-testid="clients-loading">Carregando...</div>) : filtered.length === 0 ? (<div className="soft-card p-10 text-center" data-testid="clients-empty"><div className="text-5xl mb-3" aria-hidden></div><div className="text-lg font-semibold text-slate-900">{clients.length === 0 ? "Nenhum cliente ainda" : "Nenhum resultado"}</div><p className="text-slate-500 mt-1">{clients.length === 0 ? "Cadastre seu primeiro cliente." : "Tente outra busca."}</p></div>) : (<div className="space-y-5" data-testid="clients-groups">{groupKeys.map((key) =>{
 const list = groups.get(key);
 const label = key === "__none__" ? "Sem família" : key;
 return (<div key={key} className="soft-card overflow-hidden"
 data-testid={`family-group-${key === "__none__" ? "none" : key}`}><div className="px-4 py-2.5 bg-blue-50/60 border-b border-slate-100 flex items-center justify-between"><div className="font-semibold text-[#0b3a82] text-sm flex items-center gap-2"><span aria-hidden>‍‍</span>{label}</div><span className="text-xs text-slate-500">{list.length} cliente(s)</span></div><div>{list.map((c) =>(<ClientRow
 key={c.client_id}
 c={c}
 expanded={expandedId === c.client_id}
 onToggle={() =>setExpandedId(expandedId === c.client_id ? null : c.client_id)}
 onEdit={(cli) =>{ setEditing(cli); setOpenForm(true); }}
 onDelete={(cli) =>setDeleting(cli)}
 selected={selectedIds.includes(c.client_id)}
 onSelectToggle={toggleSelect}
 />))}</div></div>);
 })}</div>)}</TabsContent><TabsContent value="birthdays" className="mt-0"><BirthdaysTab /></TabsContent></Tabs><ClientFormDialog
 open={openForm}
 onOpenChange={setOpenForm}
 client={editing}
 existingFamilies={Array.from(new Set(clients.map(c =>c.family).filter(Boolean)))}
 onSaved={() =>{ setOpenForm(false); load(); onChange?.(); }}
 /><AlertDialog open={!!deleting} onOpenChange={(o) =>!o && setDeleting(null)}><AlertDialogContent data-testid="delete-client-dialog"><AlertDialogHeader><AlertDialogTitle>Excluir cliente?</AlertDialogTitle><AlertDialogDescription>Esta ação remove o cliente das emissões vinculadas e exclui emissões que ficarem sem passageiros.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel data-testid="delete-client-cancel">Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700"
 data-testid="delete-client-confirm">Excluir</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog></div>);
}
